import axios from 'axios';

const API_URL = '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('dronemap_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for 401 handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('dronemap_token');
      localStorage.removeItem('dronemap_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth
export const login = (email: string, password: string) =>
  api.post('/auth/login', { email, password });

export const getMe = () =>
  api.get('/auth/me');

export const changePassword = (currentPassword: string, newPassword: string) =>
  api.post('/auth/change-password', { currentPassword, newPassword });

// Clients
export const getClients = () =>
  api.get('/clients');

export const getClient = (id: number) =>
  api.get(`/clients/${id}`);

export const createClient = (data: { name: string; slug: string; contact_email?: string; storage_quota_gb?: number }) =>
  api.post('/clients', data);

// Projects
export const getProjects = () =>
  api.get('/projects');

export const getProject = (id: number) =>
  api.get(`/projects/${id}`);

export const createProject = (data: { client_id: number; name: string; description?: string; location_lat?: number; location_lon?: number }) =>
  api.post('/projects', data);

// Uploads
export const getUploads = (projectId?: number) =>
  api.get('/uploads', { params: { project_id: projectId } });

export const getUpload = (id: number) =>
  api.get(`/uploads/${id}`);

export const getUploadStatus = (id: number) =>
  api.get(`/uploads/${id}/status`);

export const createUpload = (formData: FormData, onProgress?: (percent: number) => void) =>
  api.post('/uploads', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress ? (progressEvent) => {
      const percent = progressEvent.total
        ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
        : 0;
      onProgress(percent);
    } : undefined,
  });

// Tiles — include JWT token as query param for MapLibre img tag requests
export const getTileUrl = (uploadId: number, layer: string) => {
  const token = localStorage.getItem('dronemap_token');
  return `${API_URL}/tiles/${uploadId}/${layer}/{z}/{x}/{y}.png${token ? `?token=${token}` : ''}`;
};

export const getPixelValue = (uploadId: number, layer: string, lon: number, lat: number) =>
  api.get(`/tiles/${uploadId}/pixel`, { params: { layer, lon, lat } });

// Features
export const getFeatures = (uploadId: number) =>
  api.get(`/features/upload/${uploadId}`);

// Audit
export const getAuditLogs = (params?: { action?: string; user_id?: number }) =>
  api.get('/audit', { params });

// ROI Exports
export const requestExport = (data: { upload_id: number; roi_geojson: any; layer_name?: string; file_format?: string }) =>
  api.post('/exports', data);

export const getExport = (id: number) =>
  api.get(`/exports/${id}`);

export const getUploadExports = (uploadId: number) =>
  api.get(`/exports/upload/${uploadId}`);

// Users
export const getUsers = (params?: { client_id?: number; role?: string }) =>
  api.get('/users', { params });

export const getUser = (id: number) =>
  api.get(`/users/${id}`);

export const createUser = (data: { email: string; password: string; first_name: string; last_name: string; role: string; client_id?: number }) =>
  api.post('/users', data);

export const updateUser = (id: number, data: { first_name?: string; last_name?: string; role?: string; is_active?: boolean; client_id?: number }) =>
  api.put(`/users/${id}`, data);

export const deleteUser = (id: number) =>
  api.delete(`/users/${id}`);

